import React, { useState, useEffect } from 'react';
import {
  Check, ChevronRight, Car, Calendar, MapPin, User,
  Clock, Wrench, AlertCircle, RefreshCw
} from 'lucide-react';
import { useAuth }           from '../context/AuthContext';
import { getActiveServices, getCarBrands } from '../api';
import './BookingPage.css';

/* ── Static constants ────────────────────────────────────── */
const TIME_SLOTS = [
  '8:00 AM','9:00 AM','10:00 AM','11:00 AM','12:00 PM',
  '2:00 PM','3:00 PM','4:00 PM','5:00 PM','6:00 PM',
];

const FUEL_TYPES = [
  { id:'Petrol',   icon:'⛽' },
  { id:'Diesel',   icon:'🛢️' },
  { id:'CNG',      icon:'🔵' },
  { id:'Electric', icon:'⚡' },
  { id:'Hybrid',   icon:'🌿' },
  { id:'LPG',      icon:'🟡' },
];

const STEPS = [
  { label:'Vehicle',  icon:<Car size={16}/>      },
  { label:'Service',  icon:<Wrench size={16}/>   },
  { label:'Schedule', icon:<Calendar size={16}/> },
  { label:'Pickup',   icon:<MapPin size={16}/>   },
  { label:'Confirm',  icon:<Check size={16}/>    },
];

/* ── toUpper: apply uppercase to vehicle text fields ─────── */
const toUpper = v => v.toUpperCase();

/* ── Validation ─────────────────────────────────────────── */
function validateStep(step, form) {
  const e = {};
  if (step === 0) {
    if (!form.vehicleMake.trim())  e.vehicleMake  = 'Vehicle make is required.';
    if (!form.vehicleModel.trim()) e.vehicleModel = 'Vehicle model is required.';
    if (!form.regNumber.trim())    e.regNumber    = 'Registration number is required.';
    if (!form.fuelType)            e.fuelType     = 'Please select a fuel type.';
  }
  if (step === 1) {
    if (form.selectedServices.length === 0) e.services = 'Please select at least one service.';
  }
  if (step === 2) {
    if (!form.date)     e.date     = 'Please select a date.';
    if (!form.timeSlot) e.timeSlot = 'Please select a time slot.';
  }
  if (step === 3) {
    if (form.pickupType === 'pickup' && !form.address.trim())
      e.address = 'Pickup address is required.';
  }
  return e;
}

/* ── Brand logo component with imageUrl fallback ────────── */
function BrandChip({ brand }) {
  const [imgOk, setImgOk] = React.useState(true);
  React.useEffect(() => setImgOk(true), [brand.imageUrl]);

  const showImg = brand.imageUrl && imgOk;
  return (
    <div
      className="booking-brand"
      title={brand.name}
      style={{ width:36, height:36 }}
    >
      {showImg ? (
        <img
          src={brand.imageUrl}
          alt={brand.name}
          style={{ width:28, height:28, objectFit:'contain' }}
          onError={() => setImgOk(false)}
        />
      ) : (
        <div style={{
          width:28, height:28, borderRadius:6,
          background: brand.color || '#1C69D4',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:9, fontWeight:800, color:'#fff', lineHeight:1,
        }}>
          {brand.abbr || brand.name?.slice(0,2).toUpperCase()}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN BOOKING PAGE
══════════════════════════════════════════════════════════ */
export default function BookingPage() {
  const { user, openLogin, addBooking } = useAuth();

  /* ── Remote data ── */
  const [services,      setServices]      = useState([]);
  const [carBrands,     setCarBrands]     = useState([]);
  const [loadingData,   setLoadingData]   = useState(true);

  /* ── Form state ── */
  const [step,             setStep]             = useState(0);
  const [fieldErrors,      setFieldErrors]      = useState({});
  const [submitting,       setSubmitting]       = useState(false);
  const [submitError,      setSubmitError]      = useState('');
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  const [form, setForm] = useState({
    ownerName: user?.name  || '',
    phone:     user?.phone || '',
    email:     user?.email || '',
    vehicleMake: '', vehicleModel: '', vehicleYear: '', regNumber: '', fuelType: '',
    selectedServices: [], additionalNotes: '',
    date: '', timeSlot: '',
    pickupType: 'pickup', address: '', landmark: '',
  });

  /* ── Fetch services and car brands from backend ── */
  useEffect(() => {
    setLoadingData(true);
    Promise.all([
      getActiveServices().catch(() => []),
      getCarBrands().catch(() => []),
    ]).then(([svcs, brands]) => {
      setServices(svcs || []);
      setCarBrands(brands || []);
    }).finally(() => setLoadingData(false));
  }, []);

  /* Gate — must be signed in */
  if (!user) {
    return (
      <div className="booking-page" style={{ paddingTop:72 }}>
        <div className="booking-gate">
          <div className="booking-gate__icon"><Wrench size={32}/></div>
          <h2 className="booking-gate__title">Sign in to Book a Service</h2>
          <p className="booking-gate__sub">
            Please sign in or create a free account to continue booking.
          </p>
          <div className="booking-gate__actions">
            <button className="btn btn-primary" onClick={openLogin}>Sign In</button>
            <button className="btn btn-outline" onClick={openLogin}>Create Account</button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Field helpers ── */
  const update = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (fieldErrors[field]) setFieldErrors(e => { const n={...e}; delete n[field]; return n; });
  };

  /* uppercase fields that should always be uppercase */
  const updateUpper = (field, value) => update(field, toUpper(value));

  const toggleService = svc => {
    setForm(f => ({
      ...f,
      selectedServices: f.selectedServices.includes(svc)
        ? f.selectedServices.filter(s => s !== svc)
        : [...f.selectedServices, svc],
    }));
    if (fieldErrors.services) setFieldErrors(e => { const n={...e}; delete n.services; return n; });
  };

  const handleNext = () => {
    const errs = validateStep(step, form);
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setStep(s => s + 1);
  };

  const handlePrev = () => { setFieldErrors({}); setStep(s => s - 1); };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const booking = await addBooking({
        ownerName: form.ownerName,
        phone:     form.phone,
        email:     form.email,
        vehicleMake:  form.vehicleMake,
        vehicleModel: form.vehicleModel,
        vehicleYear:  form.vehicleYear,
        regNumber:    form.regNumber,
        fuelType:     form.fuelType,
        services:     form.selectedServices,
        additionalNotes: form.additionalNotes,
        date:      form.date,
        timeSlot:  form.timeSlot,
        pickupType: form.pickupType,
        address:   form.address,
        landmark:  form.landmark,
      });
      setConfirmedBooking(booking);
    } catch (e) {
      setSubmitError(e?.response?.data?.message || e?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setConfirmedBooking(null); setStep(0); setFieldErrors({}); setSubmitError('');
    setForm({
      ownerName: user?.name||'', phone: user?.phone||'', email: user?.email||'',
      vehicleMake:'', vehicleModel:'', vehicleYear:'', regNumber:'', fuelType:'',
      selectedServices:[], additionalNotes:'',
      date:'', timeSlot:'',
      pickupType:'pickup', address:'', landmark:'',
    });
  };

  /* ── Field error renderer ── */
  const FErr = ({ field }) => fieldErrors[field]
    ? <span className="field-error"><AlertCircle size={11}/>{fieldErrors[field]}</span>
    : null;

  /* ── Success screen ── */
  if (confirmedBooking) {
    return (
      <div className="booking-page" style={{ paddingTop:72 }}>
        <div className="booking-success">
          <div className="booking-success__icon"><Check size={36}/></div>
          <h2>Booking Submitted!</h2>
          <p>Your booking reference is <strong>{confirmedBooking.id}</strong></p>
          <p>
            Your booking is <strong>awaiting confirmation</strong> from our team.
            You'll be notified at <strong>{form.email}</strong>
          </p>
          <div className="booking-success__details">
            <div>
              <span>Vehicle</span>
              <span>{form.vehicleMake} {form.vehicleModel} · {form.regNumber}</span>
            </div>
            <div><span>Fuel Type</span><span>{form.fuelType}</span></div>
            <div>
              <span>Service</span>
              <span>
                {form.selectedServices[0]}
                {form.selectedServices.length > 1 ? ` +${form.selectedServices.length-1} more` : ''}
              </span>
            </div>
            <div><span>Date & Time</span><span>{form.date} at {form.timeSlot}</span></div>
            <div>
              <span>Pickup</span>
              <span>
                {form.pickupType==='self'    ? 'Self Drive-in'      :
                 form.pickupType==='pickup'  ? 'Vehicle Pickup'     :
                                               'Customer Drop-off'}
              </span>
            </div>
          </div>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <button className="btn btn-primary" onClick={resetForm}>Book Another</button>
            <button className="btn btn-outline" onClick={() => window.location.href='/'}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Pickup label helper ── */
  const pickupLabel = t =>
    t === 'self'    ? 'Self Drive-in'      :
    t === 'pickup'  ? 'Vehicle Pickup'     :
    t === 'dropoff' ? 'Customer Drop-off'  : t;

  /* ── Service name list for grid ── */
  // Use API service names, always include "Other" at end
  const serviceNames = loadingData
    ? []
    : [
        ...services.filter(s => s.active !== false).map(s => s.name),
        'Other',
      ];

  /* ════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════ */
  return (
    <div className="booking-page" style={{ paddingTop:72 }}>
      {/* Header */}
      <div className="booking-header">
        <img src="/images/booking-banner.jpg" alt="" className="booking-header__bg-img" aria-hidden="true"/>
        <div className="container">
          <h1 className="booking-header__title">Book a Service</h1>
          <p className="booking-header__sub">Complete the form below — takes less than 3 minutes.</p>
        </div>
      </div>

      <div className="container">
        <div className="booking-layout">

          {/* ── Stepper ── */}
          <div className="booking-stepper">
            {STEPS.map(({ label, icon }, i) => (
              <div key={label} className={`booking-step ${i===step?'active':''} ${i<step?'done':''}`}>
                <div className="booking-step__circle">
                  {i < step ? <Check size={14}/> : icon}
                </div>
                <span className="booking-step__label">{label}</span>
                {i < STEPS.length - 1 && <div className="booking-step__line"/>}
              </div>
            ))}
          </div>

          {/* ── Form ── */}
          <div className="booking-form-wrap">
            <div className="booking-form card">

              {/* ─────────────────────────────────────
                  STEP 0 — Vehicle Details
              ───────────────────────────────────── */}
              {step === 0 && (
                <div className="booking-section animate-fade">
                  <h3 className="booking-section__title">Vehicle Details</h3>

                  {/* Account info */}
                  <div className="booking-account-info">
                    <div className="booking-account-info__label">Booking for</div>
                    <div className="booking-account-info__fields">
                      <div className="booking-account-info__field">
                        <span className="booking-account-info__field-label">Name</span>
                        <span className="booking-account-info__field-value">{user.name}</span>
                      </div>
                      <div className="booking-account-info__field">
                        <span className="booking-account-info__field-label">Phone</span>
                        <span className="booking-account-info__field-value">
                          {user.phone || 'Not on account'}
                        </span>
                      </div>
                      <div className="booking-account-info__field">
                        <span className="booking-account-info__field-label">Email</span>
                        <span className="booking-account-info__field-value">{user.email}</span>
                      </div>
                    </div>
                    <p className="booking-account-info__hint">
                      <AlertCircle size={11}/> Pulled from your account — update in profile settings.
                    </p>
                  </div>

                  {/* Brand logos — fetched from backend */}
                  {carBrands.length > 0 && (
                    <div className="booking-brands">
                      {carBrands.map(b => <BrandChip key={b.id} brand={b}/>)}
                    </div>
                  )}

                  <div className="form-grid">
                    {/* Vehicle Make — UPPERCASE */}
                    <div className="form-field">
                      <label className="label">Vehicle Make *</label>
                      <input
                        className={`input ${fieldErrors.vehicleMake ? 'input--error' : ''}`}
                        placeholder="e.g. BMW, AUDI, HONDA"
                        value={form.vehicleMake}
                        onChange={e => updateUpper('vehicleMake', e.target.value)}
                        style={{ textTransform:'uppercase' }}
                      />
                      <FErr field="vehicleMake"/>
                    </div>

                    {/* Vehicle Model — UPPERCASE */}
                    <div className="form-field">
                      <label className="label">Vehicle Model *</label>
                      <input
                        className={`input ${fieldErrors.vehicleModel ? 'input--error' : ''}`}
                        placeholder="e.g. 3 SERIES, CITY"
                        value={form.vehicleModel}
                        onChange={e => updateUpper('vehicleModel', e.target.value)}
                        style={{ textTransform:'uppercase' }}
                      />
                      <FErr field="vehicleModel"/>
                    </div>

                    {/* Year — unchanged */}
                    <div className="form-field">
                      <label className="label">Year <span className="label-optional">(optional)</span></label>
                      <input className="input" placeholder="e.g. 2021"
                        maxLength={4}
                        value={form.vehicleYear}
                        onChange={e => update('vehicleYear', e.target.value.replace(/\D/g,''))}
                      />
                    </div>

                    {/* Registration — UPPERCASE */}
                    <div className="form-field">
                      <label className="label">Registration Number *</label>
                      <input
                        className={`input ${fieldErrors.regNumber ? 'input--error' : ''}`}
                        placeholder="e.g. PB10 AA 1234"
                        value={form.regNumber}
                        onChange={e => updateUpper('regNumber', e.target.value)}
                        style={{ textTransform:'uppercase', letterSpacing:'0.05em' }}
                      />
                      <FErr field="regNumber"/>
                    </div>

                    {/* Fuel Type */}
                    <div className="form-field form-field--full">
                      <label className="label">Fuel Type *</label>
                      <div className={`fuel-type-grid ${fieldErrors.fuelType ? 'fuel-type-grid--error' : ''}`}>
                        {FUEL_TYPES.map(({ id, icon }) => (
                          <button
                            key={id} type="button"
                            className={`fuel-btn ${form.fuelType === id ? 'fuel-btn--selected' : ''}`}
                            onClick={() => update('fuelType', id)}
                          >
                            <span className="fuel-btn__icon">{icon}</span>
                            <span className="fuel-btn__label">{id}</span>
                            {form.fuelType === id && (
                              <span className="fuel-btn__check"><Check size={11}/></span>
                            )}
                          </button>
                        ))}
                      </div>
                      <FErr field="fuelType"/>
                    </div>
                  </div>
                </div>
              )}

              {/* ─────────────────────────────────────
                  STEP 1 — Service Selection
              ───────────────────────────────────── */}
              {step === 1 && (
                <div className="booking-section animate-fade">
                  <h3 className="booking-section__title">Select Services</h3>
                  <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:16 }}>
                    You may select multiple services.
                  </p>

                  {loadingData ? (
                    /* Loading skeleton */
                    <div className="service-select-grid">
                      {[1,2,3,4,5,6,7,8].map(i => (
                        <div key={i} style={{
                          height:44, borderRadius:'var(--radius-md)',
                          background:'var(--bg-secondary)',
                          animation:'shimmer 1.4s infinite',
                          backgroundSize:'200% 100%',
                          backgroundImage:'linear-gradient(90deg,var(--bg-secondary) 25%,var(--border) 50%,var(--bg-secondary) 75%)',
                        }}/>
                      ))}
                    </div>
                  ) : (
                    <div className={`service-select-grid ${fieldErrors.services ? 'service-select-grid--error' : ''}`}>
                      {serviceNames.map(svc => (
                        <button
                          key={svc}
                          className={`service-select-item ${form.selectedServices.includes(svc) ? 'selected' : ''}`}
                          onClick={() => toggleService(svc)}
                        >
                          {form.selectedServices.includes(svc) && <Check size={14}/>}
                          {svc}
                        </button>
                      ))}
                    </div>
                  )}

                  <FErr field="services"/>

                  <div className="form-field" style={{ marginTop:18 }}>
                    <label className="label">
                      Additional Notes <span className="label-optional">(optional)</span>
                    </label>
                    <textarea className="input" rows={3}
                      placeholder="Describe any issues or special requests…"
                      value={form.additionalNotes}
                      onChange={e => update('additionalNotes', e.target.value)}
                      style={{ resize:'vertical', minHeight:80 }}
                    />
                  </div>
                </div>
              )}

              {/* ─────────────────────────────────────
                  STEP 2 — Schedule
              ───────────────────────────────────── */}
              {step === 2 && (
                <div className="booking-section animate-fade">
                  <h3 className="booking-section__title">Choose Date & Time</h3>
                  <div className="form-grid">
                    <div className="form-field form-field--full">
                      <label className="label">Preferred Date *</label>
                      <input
                        className={`input ${fieldErrors.date ? 'input--error' : ''}`}
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        value={form.date}
                        onChange={e => update('date', e.target.value)}
                      />
                      <FErr field="date"/>
                    </div>
                  </div>
                  <div style={{ marginTop:20 }}>
                    <label className="label">Time Slot *</label>
                    <div className={`time-slots ${fieldErrors.timeSlot ? 'time-slots--error' : ''}`}>
                      {TIME_SLOTS.map(slot => (
                        <button
                          key={slot}
                          className={`time-slot ${form.timeSlot === slot ? 'selected' : ''}`}
                          onClick={() => update('timeSlot', slot)}
                        >
                          <Clock size={12}/> {slot}
                        </button>
                      ))}
                    </div>
                    <FErr field="timeSlot"/>
                  </div>
                </div>
              )}

              {/* ─────────────────────────────────────
                  STEP 3 — Pickup
              ───────────────────────────────────── */}
              {step === 3 && (
                <div className="booking-section animate-fade">
                  <h3 className="booking-section__title">Pickup / Drop-off</h3>
                  <img src="/images/booking-pickup-illustration.png" alt=""
                    className="booking-pickup-illustration"
                    onError={e => { e.target.style.display='none'; }}/>
                  <div className="pickup-options">
                    {[
                      { value:'pickup',  label:'Vehicle Pickup',    desc:'Our driver collects your car from your address.', icon:<Car size={20}/> },
                      { value:'dropoff', label:'Customer Drop-off', desc:'You drop your car at our service centre.',        icon:<MapPin size={20}/> },
                      { value:'self',    label:'Self Drive-in',     desc:'Drive in yourself at the scheduled time.',        icon:<User size={20}/> },
                    ].map(({ value, label, desc, icon }) => (
                      <button key={value}
                        className={`pickup-option ${form.pickupType === value ? 'selected' : ''}`}
                        onClick={() => update('pickupType', value)}>
                        <div className="pickup-option__icon">{icon}</div>
                        <div>
                          <p className="pickup-option__label">{label}</p>
                          <p className="pickup-option__desc">{desc}</p>
                        </div>
                        <div className="pickup-option__check">
                          {form.pickupType === value && <Check size={14}/>}
                        </div>
                      </button>
                    ))}
                  </div>

                  {form.pickupType === 'pickup' && (
                    <div className="form-grid" style={{ marginTop:20 }}>
                      <div className="form-field form-field--full">
                        <label className="label">Pickup Address *</label>
                        <input
                          className={`input ${fieldErrors.address ? 'input--error' : ''}`}
                          placeholder="House / Flat No., Street, Area"
                          value={form.address}
                          onChange={e => update('address', e.target.value)}
                        />
                        <FErr field="address"/>
                      </div>
                      <div className="form-field form-field--full">
                        <label className="label">
                          Landmark <span className="label-optional">(optional)</span>
                        </label>
                        <input className="input" placeholder="Near, opposite…"
                          value={form.landmark}
                          onChange={e => update('landmark', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ─────────────────────────────────────
                  STEP 4 — Confirm
              ───────────────────────────────────── */}
              {step === 4 && (
                <div className="booking-section animate-fade">
                  <h3 className="booking-section__title">Confirm Booking</h3>
                  <div className="confirm-summary">
                    <div className="confirm-row"><span>Owner</span><span>{form.ownerName}</span></div>
                    <div className="confirm-row"><span>Phone</span><span>{form.phone || '—'}</span></div>
                    <div className="confirm-row"><span>Email</span><span>{form.email}</span></div>
                    <div className="confirm-row">
                      <span>Vehicle</span>
                      <span>{form.vehicleMake} {form.vehicleModel}{form.vehicleYear ? ` (${form.vehicleYear})` : ''}</span>
                    </div>
                    <div className="confirm-row"><span>Reg. No.</span><span>{form.regNumber}</span></div>
                    <div className="confirm-row"><span>Fuel Type</span><span>{form.fuelType}</span></div>
                    <div className="confirm-row">
                      <span>Services</span><span>{form.selectedServices.join(', ')}</span>
                    </div>
                    <div className="confirm-row">
                      <span>Date & Time</span><span>{form.date} at {form.timeSlot}</span>
                    </div>
                    <div className="confirm-row">
                      <span>Pickup</span><span>{pickupLabel(form.pickupType)}</span>
                    </div>
                    {form.address && (
                      <div className="confirm-row"><span>Address</span><span>{form.address}</span></div>
                    )}
                    {form.additionalNotes && (
                      <div className="confirm-row"><span>Notes</span><span>{form.additionalNotes}</span></div>
                    )}
                  </div>

                  {submitError && (
                    <div style={{
                      display:'flex', alignItems:'center', gap:8,
                      margin:'14px 0', padding:'10px 14px',
                      background:'rgba(220,38,38,0.08)',
                      border:'1px solid rgba(220,38,38,0.25)',
                      borderRadius:'var(--radius-md)',
                      fontSize:13, color:'var(--danger)',
                    }}>
                      <AlertCircle size={14}/> {submitError}
                    </div>
                  )}

                  <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:14, lineHeight:1.6 }}>
                    By confirming, you agree to our service terms. A mechanic will be assigned
                    and you'll receive a confirmation SMS and email.
                  </p>
                </div>
              )}

              {/* ── Navigation ── */}
              <div className="booking-nav">
                {step > 0 && (
                  <button className="btn btn-ghost" onClick={handlePrev} disabled={submitting}>
                    ← Back
                  </button>
                )}
                <div style={{ marginLeft:'auto' }}>
                  {step < 4 ? (
                    <button className="btn btn-primary" onClick={handleNext}>
                      Continue <ChevronRight size={16}/>
                    </button>
                  ) : (
                    <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                      {submitting
                        ? <><RefreshCw size={15} className="spinning"/> Submitting…</>
                        : <><Check size={16}/> Confirm Booking</>
                      }
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spinning{animation:spin .8s linear infinite; display:inline-block;}
        .animate-fade{animation:fadeUp .2s ease}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
      `}</style>
    </div>
  );
}